import { FC, useContext, useMemo, useState, SetStateAction, Dispatch, useEffect } from 'react';
// formik
import { Field, FieldProps, Form, Formik } from 'formik';
// API
import { API } from 'core/API';
// redux
import { useDispatch } from 'react-redux';
import { updateVolunteer } from 'redux/volunteer-service/actions';
import { setError } from 'redux/error-service/action';
// validation
import { emailSchema, userVolunteerInfoFormValidationSchema } from 'core/validation';
// types
import {
	IBasicVolunteerResponse,
	IGetVolunteerByIdResponse,
	IGradeResponse,
	IUpdatePasswordRequest,
	IUpdateVolunteerRequest,
	ValidateUserEmailRequest,
	ValidateUserPhoneNumberRequest,
} from '@joc/api-gateway';
// context
import { VolunteerTableDataContext } from 'components/Organization/Volunteers/Table';
// helpers
import { getInitialValues, getSchoolGrades } from './helpers';
// components
import Loader from 'shared/components/Loader';
import GenderSelect from 'shared/components/Selects/SelectDropdown/GenderSelect';
import GoogleApiAutoCompleat from 'shared/components/GoogleApiAutoComplete';
import InputContainer from 'shared/components/FormInputs/InputContainer';
import InlineDatePicker from 'shared/components/Pickers/InlineDatePicker';
import UploadUserPhoto from 'components/Volunteer/UploadUserPhoto';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputPhone from 'shared/components/FormInputs/InputPhone';
import InputText from 'shared/components/FormInputs/InputText';
import InputErrorHint from 'shared/components/InputErrorHint';
// import GradeSelect from 'shared/components/Selects/SelectDropdown/GradeSelect';
import InputPassword from 'shared/components/FormInputs/InputPassword';
import { isPhoneNumberValid, normalizePhoneNumber } from 'core/functions';
// styles
import styles from './VolunteerInfoForm.module.scss';
import InputSearchOrg from '../../InputSearchOrg';

interface IVolunteerInfoFormProps {
	setIsUpdateSuccess: Dispatch<SetStateAction<boolean>>;
}

const VolunteerInfoForm: FC<IVolunteerInfoFormProps> = ({ setIsUpdateSuccess }) => {
	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [emailAlreadyExists, setEmailError] = useState('');
	const [phoneAlreadyExists, setPhoneError] = useState('');
	const [, setGrades] = useState<Array<IGradeResponse>>();

	const [volunteerInfo, setVolunteerInfo] = useState<IGetVolunteerByIdResponse>();

	const volunteerData: IBasicVolunteerResponse = useContext(VolunteerTableDataContext);
	const initialValues = useMemo(() => getInitialValues(volunteerData), [volunteerData]);

	const handleGetVolunteerAndGrades = async () => {
		setIsLoading(true);
		try {
			const volunteerInfoData = await API.getVolunteerById(Number(volunteerData.id));
			const volunteerGrades = await getSchoolGrades(volunteerInfoData);

			if (volunteerInfoData) setVolunteerInfo(volunteerInfoData);
			if (volunteerGrades) setGrades(volunteerGrades);
		} catch (error: any) {
			dispatch(setError(error?.response?.message || error.response, error?.response?.code || error?.code));
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		handleGetVolunteerAndGrades().finally();
	}, []);

	if (!volunteerInfo) return <Loader loadProps={{ disableCenterStyle: true }} />;

	const validatePhoneUniqueness = async (phoneNumber: string): Promise<void> => {
		const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
		const initialPhoneNumber = normalizePhoneNumber(volunteerData.phoneNumber);
		const isPhoneChanged = normalizedPhoneNumber !== initialPhoneNumber;
		const shouldValidatePhone =
			!!normalizedPhoneNumber && isPhoneChanged && isPhoneNumberValid(normalizedPhoneNumber);

		if (!shouldValidatePhone) {
			return;
		}

		await API.validatePhoneNumber(ValidateUserPhoneNumberRequest.fromJS({ phoneNumber: normalizedPhoneNumber }));
	};

	const handleSubmit = async (values: IUpdateVolunteerRequest & IUpdatePasswordRequest) => {
		setIsLoading(true);
		try {
			const { id, userId, email } = volunteerInfo;
			const normalizedPhoneNumber = normalizePhoneNumber(values.phoneNumber);
			const normalizedEmail = values.email?.trim() || '';
			const initialEmail = email?.trim() || '';

			if (normalizedEmail && normalizedEmail !== initialEmail) {
				await API.changeEmailByAdmin(Number(id), ValidateUserEmailRequest.fromJS({ email: normalizedEmail }));
			}
			await validatePhoneUniqueness(values.phoneNumber || '');

			dispatch(
				updateVolunteer(Number(id), Number(userId), {
					...values,
					email: normalizedEmail || undefined,
					phoneNumber: normalizedPhoneNumber || undefined,
				})
			);

			setIsUpdateSuccess(true);
		} catch (error: any) {
			setIsLoading(false);
			if (error?.response?.code === 409) {
				setPhoneError('Phone number already exists');
			}
			dispatch(setError(error?.response?.message || error.response, error?.response?.code || error?.code));
		} finally {
			setIsLoading(false);
		}
	};

	const onBlurEmailHandler = async (email?: string) => {
		try {
			setEmailError('');
			const normalizedEmail = email?.trim() || '';
			const isValid = await emailSchema.isValid(normalizedEmail);
			if (normalizedEmail && isValid && normalizedEmail !== (volunteerData.email || ''))
				await API.validateUser(ValidateUserEmailRequest.fromJS({ email: normalizedEmail }));
		} catch (error) {
			if ((error as any).response.code === 409) {
				setEmailError('Email already exists');
			} else {
				setEmailError((error as any).response.message);
			}
		}
	};

	const onBlurPhoneHandler = async (phoneNumber: string) => {
		try {
			setPhoneError('');
			await validatePhoneUniqueness(phoneNumber);
		} catch (error: any) {
			if (error?.response?.code === 409) {
				setPhoneError('Phone number already exists');
			} else {
				setPhoneError(error?.response?.message || error?.message);
			}
		}
	};

	return (
		<Formik
			enableReinitialize={true}
			initialValues={initialValues}
			validationSchema={userVolunteerInfoFormValidationSchema}
			onSubmit={handleSubmit}
		>
			{({ errors, touched, setFieldValue, setFieldTouched, dirty }) => (
				<Form className={styles.form}>
					<Field name="imagePath">
						{({ field }: FieldProps) => (
							<InputContainer parentClassnames={styles.upload_photo}>
								<UploadUserPhoto
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
									fieldName={field.name}
									fieldValue={field.value}
								/>
							</InputContainer>
						)}
					</Field>
					<Field name="firstName">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="firstName">
									First Name
								</label>
								<InputText
									id="firstName"
									field={field}
									placeholder="First name"
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.firstName && touched.firstName && (
									<InputErrorHint disableMargin={true} errorText={errors.firstName} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="lastName">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="lastName">
									Last Name
								</label>
								<InputText
									id="lastName"
									field={field}
									placeholder="Last name"
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{errors.lastName && touched.lastName && (
									<InputErrorHint disableMargin={true} errorText={errors.lastName} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="email">
						{({ field, meta, form }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="email">
									Email
								</label>
								<InputText
									id="email"
									field={field}
									placeholder="Email"
									onBlur={() => {
										onBlurEmailHandler(field.value).finally();
										form.setFieldTouched(field.name, true, true).finally();
									}}
									fieldValue={field.value}
									setFieldValue={setFieldValue}
								/>
								{(emailAlreadyExists || errors.email) && meta.touched && (
									<InputErrorHint
										disableMargin={true}
										errorText={errors.email || emailAlreadyExists}
									/>
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="phoneNumber">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="phoneNumber">
									Phone Number
								</label>
								<InputPhone
									fieldName={field.name}
									fieldValue={field.value}
									setFieldValue={(fieldName, value, shouldValidate) => {
										setPhoneError('');
										setFieldValue(fieldName, value, shouldValidate);
									}}
									setFieldTouched={setFieldTouched}
									onBlur={() => onBlurPhoneHandler(field.value || '')}
								/>
								{(errors.phoneNumber || phoneAlreadyExists) && touched.phoneNumber && (
									<InputErrorHint
										disableMargin={true}
										errorText={errors.phoneNumber || phoneAlreadyExists}
									/>
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="birthDate">
						{({ field }: FieldProps) => (
							<InputContainer isSmallHeight={true}>
								<label className={styles.label} htmlFor="birthDate">
									Date of Birth
								</label>
								<InlineDatePicker
									fieldName={field.name}
									setFieldValue={setFieldValue}
									parentClassNames={styles.birthDate}
									fieldValue={field.value}
									placeholder="Date of birth"
									availablePast={true}
								/>
								{errors.birthDate && touched.birthDate && (
									<InputErrorHint disableMargin={true} errorText={errors.birthDate as string} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="gender">
						{({ field }: FieldProps) => (
							<InputContainer isSmallHeight={true}>
								<label className={styles.label} htmlFor="gender">
									Gender
								</label>
								<GenderSelect
									placeholder="Gender"
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									fieldName={field.name}
									fieldValue={field.value}
									parentClassName={styles.gender}
								/>
								{errors.gender && touched.gender && (
									<InputErrorHint disableMargin={true} errorText={errors.gender} />
								)}
							</InputContainer>
						)}
					</Field>
					<Field name="address">
						{({ field }: FieldProps) => (
							<InputContainer isSmallHeight={true}>
								<label className={styles.label} htmlFor="address">
									Address
								</label>
								<GoogleApiAutoCompleat
									placeholder="Address"
									fieldName={field.name}
									selectHandler={address => setFieldValue(field.name, address)}
									setFieldTouched={setFieldTouched}
									setFieldValue={setFieldValue}
									fieldValue={field.value}
								/>
								{errors.address && touched.address && (
									<InputErrorHint disableMargin={true} addressErrorText={errors.address} />
								)}
							</InputContainer>
						)}
					</Field>

					{/* {grades ? (
						<Field name="gradeId">
							{({ field }: FieldProps) => (
								<InputContainer isSmallHeight={true}>
									<label className={styles.label} htmlFor="gradeId">
										School Grade
									</label>
									<GradeSelect
										grades={grades}
										fieldValue={field.value}
										fieldName={field.name}
										setFieldValue={setFieldValue}
										setFieldTouched={setFieldTouched}
									/>
								</InputContainer>
							)}
						</Field>
					) : null} */}

					<Field name="password">
						{({ field }: FieldProps) => (
							<InputContainer>
								<label className={styles.label} htmlFor="password">
									Password
								</label>
								<InputPassword field={field} setFieldValue={setFieldValue} isShow={true} />
								{errors.password && touched.password && (
									<InputErrorHint disableMargin={true} errorText={errors.password} />
								)}
							</InputContainer>
						)}
					</Field>

					<Field name="defaultOrganizationId">
						{({ field }: FieldProps) => (
							<InputContainer parentClassnames={styles.top}>
								<label className={styles.label} htmlFor={field.name}>
									Default Organization
								</label>
								<InputSearchOrg
									fieldName={field.name}
									placeholder="Default Organization"
									fieldValue={field.value}
									selectHandler={id => setFieldValue(field.name, id)}
									userOrganizations={volunteerData.organizations}
								/>
							</InputContainer>
						)}
					</Field>

					{isLoading ? (
						<Loader loadProps={{ disableCenterStyle: true }} />
					) : (
						<ButtonDefault
							submitType={true}
							classList={['primary', 'lg']}
							title="Confirm"
							disabled={!dirty || !!emailAlreadyExists || !!phoneAlreadyExists}
						/>
					)}
				</Form>
			)}
		</Formik>
	);
};

export default VolunteerInfoForm;
